import React, { memo, useImperativeHandle, forwardRef } from 'react'
import Checkbox from '@material-ui/core/Checkbox'
import FormControlLabel from './FormControlLabel'
import PropTypes from 'prop-types'
import { withStyles } from '@material-ui/core/styles'

import { useBaseComponentOperations } from '../../hooks'
import { translate } from '../../multilanguage'
import { eventObject, defaultColor } from './common'

export const ExtCheckbox = withStyles(theme => ({
  root: {
    '&:disabled': {
      backgroundColor: theme.palette.buttonDisabledBackgroundColor,
      color: theme.palette.buttonDisabledColor,
    }
  }
}))(Checkbox)

export const BaseCheckBox = memo(props => {
  if (props.checkColor) {
    return (
      <ExtCheckbox
        id={props.id}
        checked={props.checked}
        onChange={props.onChange}
        disabled={props.disabled}
        style={{
          color: props.checkColor,
        }}
        color={props.color || 'default'}
      />
    )
  }
  return (
    <ExtCheckbox
      id={props.id}
      checked={props.checked}
      onChange={props.onChange}
      disabled={props.disabled}
      color={props.color || 'primary'}
    />  
  )
})

BaseCheckBox.propTypes = { 
  id: PropTypes.string, 
  checked: PropTypes.bool,
  checkColor: PropTypes.string,
  color: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool
}

/**
 * Implements Mui CheckBox.
 *
 * @param {object} props component props
 * @returns {CheckBox} CheckBox component
 */
const CheckBox = memo(forwardRef((props, ref) => {
  const eventKey = 'checked'
  const [value, onChange] = useBaseComponentOperations(props, eventKey, props.defaultValue !== undefined ? props.defaultValue : false)
  useImperativeHandle(ref, () => ({
    getValue: () => value,
    setValue: value => {
      onChange(eventObject(eventKey, value))
    }
  }))
  if (props.label) {
    return (
      <FormControlLabel
        control={
          <BaseCheckBox
            {...props}
            checked={value}
            onChange={onChange}
            checkColor={props.checkColor}
            disabled={props.disabled}
          />
        }
        label={typeof props.label !== 'string' ? props.label : props.noTranslate ? props.label : translate(props.label)}
      />
    )
  }
  return (
    <BaseCheckBox
      id={props?.id}
      checked={value}
      onChange={onChange}
      disabled={props.disabled}
      checkColor={props.checkColor}
      color={props.color}
    />
  )
}))

CheckBox.propTypes = {
  /**
   id for the control.
   */
  id: PropTypes.string,
  /**
   Default value for the control.
   */ 
  defaultValue: PropTypes.bool,
  /**
   Label to display for the control.
   */
  label: PropTypes.string,
  /**
   onChange function called when the checkbox is clicked.
   */
  onChange: PropTypes.func,
  /**
   Html color for the checkbox.
   */
  checkColor: PropTypes.string,
  /**
   MUI color for the checkbox.
   */
  color: PropTypes.oneOf(['primary', 'secondary', 'default']),
  /**
   Prevents the language translation of the label.
   */
  noTranslate: PropTypes.bool,
  /**
   Determines if the control is disabled.
   */
  disabled: PropTypes.bool,
}

CheckBox.defaultProps = {
  color: defaultColor,
  noTranslate: false
}

export default CheckBox